import * as React from 'react';
import {
  Controller,
  useFormContext,
} from 'react-hook-form';
import ReactInputMask from 'react-input-mask';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import {
  InputAutocompletePlaces,
  InputTextWithLabel,
  InputSelectWithLabel,
  InputBaseContainer,
} from '../molecules';
import { stepOneLabels } from './ProjectFormContainer/ProjectFormLabels';
import { US_STATES } from '../constants/usStates';

const ProjectFormStepOne = ({
  currentValidationSchema,
}: Props) => {
  const { control, errors, setValue, watch } = useFormContext();
  const labels = stepOneLabels;
  const handlePlaceSelect = (address, isSelect) => {
    if (isSelect) {
      return Object.entries(address).map(([key, value]) => {
        if (key === 'state') {
          const match = US_STATES.find(
            s =>
              s.label.toLowerCase() === String(value).toLowerCase() ||
              s.value.toLowerCase() === String(value).toLowerCase(),
          );
          return setValue(key, match?.value || value);
        }
        return setValue(key, value);
      });
    }
    return setValue('address', address);
  };

  const handleZipChange = (onChange) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
    let formatted = digits;
    if (digits.length > 5) {
      formatted = `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    onChange(formatted);
  };

  return (
    <>
      <InputBaseContainer>
        <Controller
          name="address"
          as={InputAutocompletePlaces}
          defaultValue=""
          control={control}
          helperText={errors.address?.message}
          error={errors.address?.message}
          label={labels.address}
          handleFieldChange={handlePlaceSelect}
          isRequired={isYupFieldRequired(
            'address',
            currentValidationSchema,
          )}
        />
      </InputBaseContainer>
      <InputBaseContainer>
        <Controller
          as={InputTextWithLabel}
          name="street"
          control={control}
          helperText={errors.street?.message}
          error={errors.street?.message}
          label={labels.street}
          isRequired={isYupFieldRequired(
            'street',
            currentValidationSchema,
          )}
        />
        <Controller
          as={InputTextWithLabel}
          name="city"
          control={control}
          helperText={errors.city?.message}
          error={errors.city?.message}
          label={labels.city}
          isRequired={isYupFieldRequired(
            'city',
            currentValidationSchema,
          )}
        />
      </InputBaseContainer>
      <InputBaseContainer>
        <Controller
          name="state"
          defaultValue=""
          control={control}
          render={({ onChange, onBlur, value }) => {
            const selected = US_STATES.find(
              s => s.value === value || s.label === value,
            ) || null;
            return (
              <InputSelectWithLabel
                placeholder="Select State"
                label={labels.state}
                error={errors.state}
                isRequired={isYupFieldRequired(
                  'state',
                  currentValidationSchema,
                )}
                isSearchable
                options={US_STATES}
                value={selected}
                onBlur={onBlur}
                onChange={opt => onChange(opt?.value || '')}
              />
            );
          }}
        />
        <Controller
          name="zipCode"
          defaultValue=""
          control={control}
          render={({ onChange, onBlur, value }) => (
            <InputTextWithLabel
              name="zipCode"
              label={labels.zipCode}
              error={errors.zipCode}
              isRequired={isYupFieldRequired(
                'zipCode',
                currentValidationSchema,
              )}
              value={value}
              onBlur={onBlur}
              onChange={handleZipChange(onChange)}
              inputProps={{
                inputMode: 'numeric',
                // The hyphen must be escaped: Chrome now compiles the `pattern`
                // attribute with the `v` flag, where a bare `-` inside a
                // character class is a syntax error ("Invalid character class").
                pattern: '[0-9\\-]*',
                maxLength: 10,
              }}
              placeholder="55555 or 55555-5555"
            />
          )}
        />
      </InputBaseContainer>
    </>
  );
};

interface Props {
  currentValidationSchema: any;
}

interface FieldsType {
  address: string;
  city: string;
  street: string;
  state: string;
  zipCode: string;
}

export default ProjectFormStepOne;
